import React, { useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import { styled } from '@mui/system';
import anime from 'animejs';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../styles/theme';
import BrandingSection from './BrandingSection';

// Define primary color
const primaryColor = '#FFFA66';

// Styled components
const IntroScreen = styled(Box)({
  width: '100%',
  height: '200vh',
  position: 'fixed',
  top: 0,
  left: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  overflow: 'hidden',
});

const TitleSection = styled(Box)({
  height: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: primaryColor,
  position: 'relative',
  top: '-0.2rem',
});

const ShapeSection = styled(Box)({
  width: '100%',
  margin: '-0.5rem 0 0 0',
  position: 'relative',
});

interface AnimatedIntroProps {
  onComplete: () => void;
  customTaglines?: string[];
  buttonText?: string;
}

const AnimatedIntro: React.FC<AnimatedIntroProps> = ({
  onComplete,
  customTaglines = [
    "Tenant search, simplified."
  ],
  buttonText = "Create Listing"
}) => {
  const introRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const [buttonTextState, setButtonTextState] = useState(buttonText);

  const handleStartClick = () => {
    setButtonTextState("Let's Go 🚀");

    setTimeout(() => {
      if (introRef.current && pathRef.current) {
        anime({
          targets: introRef.current,
          duration: 1000,
          easing: 'easeInOutSine',
          translateY: '-200vh',
          complete: onComplete
        });

        anime({
          targets: pathRef.current,
          duration: 1500,
          easing: 'easeInOutSine',
          d: pathRef.current.getAttribute('data-morph-path')
        });
      }
    }, 150);
  };

  return (
    <ThemeProvider theme={theme}>
      <IntroScreen ref={introRef}>
        <TitleSection>
          <BrandingSection
            animatedTaglines={true}
            customTaglines={customTaglines}
          >
            <Button
              variant="contained"
              onClick={handleStartClick}
              sx={{
                backgroundColor: 'black',
                color: 'white',
                fontSize: '1.5rem',
                padding: '6px 25px',
                borderRadius: '30px',
                fontWeight: 500,
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#333333',
                  transform: 'translateY(-2px)',
                  borderRadius: '30px',
                },
                '&.MuiButtonBase-root': {
                  borderRadius: '30px',
                },
                '&.MuiButton-root': {
                  borderRadius: '30px',
                }
              }}
            >
              {buttonTextState}
            </Button>
          </BrandingSection>
        </TitleSection>
        <ShapeSection>
          <svg width="100%" height="100vh" preserveAspectRatio="none" viewBox="0 0 1440 800">
            <path
              ref={pathRef}
              d="M -44,-50 C -52.71,28.52 15.86,8.186 184,14.69 383.3,22.39 462.5,12.58 638,14 835.5,15.6 987,6.4 1194,13.86 1661,30.68 1652,-36.74 1582,-140.1 1512,-243.5 15.88,-589.5 -44,-50 Z"
              data-morph-path="M -44,-50 C -137.1,117.4 67.86,445.5 236,452 435.3,459.7 500.5,242.6 676,244 873.5,245.6 957,522.4 1154,594 1593,753.7 1793,226.3 1582,-126 1371,-478.3 219.8,-524.2 -44,-50 Z"
              fill={primaryColor}
            />
          </svg>
        </ShapeSection>
      </IntroScreen>
    </ThemeProvider>
  );
};

export default AnimatedIntro;